import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import {
  RATING_READONLY_ARIA,
  RATING_STAR_OPTION_ARIA,
} from 'src/app/modules/route-planner/constants/multi-language.constants';

@Component({
    selector: 'app-star-rating',
    templateUrl: './star-rating.component.html',
    styleUrls: ['./star-rating.component.scss'],
    standalone: false
})
export class StarRatingComponent implements OnChanges {
  private static nextGroupId = 0;

  @Output() onRatingChanged = new EventEmitter<number>();

  @Input() rating: number;
  @Input() readOnly: boolean = false;
  /** Element id of visible instructions (e.g. popup heading) for radiogroup aria-labelledby */
  @Input() ariaLabelledBy: string | null = null;

  readonly starIndexes = [1, 2, 3, 4, 5] as const;
  readonly radioGroupName = `star-rating-${++StarRatingComponent.nextGroupId}`;
  readonly radioIdPrefix = `star-rating-opt-${StarRatingComponent.nextGroupId}-`;

  /** Hover preview only; does not change committed rating until a radio is selected */
  hoverIndex: number | null = null;

  constructor(private readonly languageService: LanguageService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rating'] && !this.readOnly) {
      this.hoverIndex = null;
    }
  }

  /** Threshold for filled stars (matches previous index <= rating behavior) */
  get displayFillThreshold(): number {
    const base = this.hoverIndex ?? this.rating;
    return base == null || base === 0 ? 0 : base;
  }

  isStarFilled(index: number): boolean {
    return !!this.displayFillThreshold && index <= this.displayFillThreshold;
  }

  /** Which radio is checked: floor of parent rating so it matches star fill (e.g. 4.3 → 4) */
  isRadioChecked(index: number): boolean {
    return index === this.radioValueFromRating(this.rating);
  }

  private radioValueFromRating(value: number | undefined): number {
    const r = Number(value);
    if (!r || r < 1) {
      return 0;
    }
    return Math.min(5, Math.floor(r));
  }

  setRating(index: number): void {
    if (!this.readOnly) {
      this.hoverIndex = null;
      this.onRatingChanged.emit(index);
    }
  }

  onStarHover(index: number): void {
    if (!this.readOnly) {
      this.hoverIndex = index;
    }
  }

  onStarGroupLeave(): void {
    this.hoverIndex = null;
  }

  starOptionAriaLabel(index: number): string {
    const lang = this.languageService.getSelectedLanguage().language;
    const tpl =
      RATING_STAR_OPTION_ARIA[lang] ?? RATING_STAR_OPTION_ARIA['english'];
    return tpl.replace('{0}', String(index));
  }

  get readOnlyAriaLabel(): string {
    const r = Number(this.rating);
    if (!r || r <= 0) {
      return '';
    }
    const lang = this.languageService.getSelectedLanguage().language;
    const tpl =
      RATING_READONLY_ARIA[lang] ?? RATING_READONLY_ARIA['english'];
    const formatted =
      Math.abs(r - Math.round(r)) < 1e-9
        ? String(Math.round(r))
        : r.toFixed(1).replace(/\.0$/, '');
    return tpl.replace('{0}', formatted);
  }
}
